import React, { lazy, Suspense, useMemo } from 'react';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import { Grid, GridItem } from 'components/UI/Grid';
import Button from 'components/UI/Button';
import Alert from 'components/UI/Alert';
import WhatWeDoCard from './WhatWeDoCard';
import MobileSlider from 'components/MobileSlider';
import { useStateModals } from 'utils/hooks';
import { useTrans } from 'utils/localisation';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './WhatWeDo.module.scss';

const RequestServicesModal = lazy(() => import('components/Modals/RequestServicesModal'));

const gridSettings = {
  xsm: { cols: 1 },
  sm: { cols: 3 },
  md: { cols: 3 },
  lg: { cols: 3 },
  xl: { cols: 3 },
  gutterX: '20px',
  gutterY: '20px',
};

function WhatWeDo({
  className,
  title,
  subtitle,
  notificationText,
  notificationButton,
  withAlert = true,
  textKey = 'requestServices',
  items,
}) {
  const t = useTrans(['common', 'whatWeDo']);
  const { modalState, showModal, hideModal } = useStateModals();
  const services = useMemo(() => items.map(item => item.title), [items]);
  const id = 'what-we-do';

  const alertButton = notificationButton ?? t(textKey);

  return (
    <>
      <Container id="what-we-do" className={className}>
        <div className={styles.wrapper}>
          {title && <Title tag="h2">{title}</Title>}
          {subtitle && <Subtitle size="lg">{subtitle}</Subtitle>}
          <div className={styles.desktopWrapper}>
            <Grid {...gridSettings}>
              {items.map((item, i) => (
                <GridItem key={`Card-${i}`}>
                  <WhatWeDoCard {...item} />
                </GridItem>
              ))}
            </Grid>
          </div>
          <MobileSlider
            className={styles.mobileWrapper}
            id="whatWeDo"
            items={items}
            CardComponent={WhatWeDoCard}
          />
          {withAlert && (
            <Alert
              className={styles.alert}
              theme="large"
              background="white"
              icon={null}
              shadow={true}
              content={notificationText || t('notificationText')}
              Button={
                <Button onClick={() => showModal('requestServices')} fullWidthMobile>
                  {alertButton}
                </Button>
              }
            />
          )}
        </div>
      </Container>
      {withAlert && (
        <Suspense fallback={null}>
          <RequestServicesModal
            title={alertButton}
            services={services}
            isOpen={modalState.requestServices}
            closeModalCallback={hideModal('requestServices')}
            animation="show"
            id={id}
            formName={id}
          />
        </Suspense>
      )}
    </>
  );
}

WhatWeDo.displayName = 'WhatWeDo';

export default withErrorBoundary(WhatWeDo);
